'use client'

import { Text, Spacer } from '@vinted/web-ui'

import { useToggleFavourite } from '@marketplace-web/item-favourites/favourite-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { brazeLogCustomEvent, useGetUserExternalId } from '@marketplace-web/braze/braze-feature'
import { BrazeCustomEvent } from '@marketplace-web/braze/braze-data'
import {
  googleTagManagerTrack,
  GoogleTagManagerEvent,
} from '@marketplace-web/google-track/google-track-feature'
import { getSearchSessionData } from '@marketplace-web/search/search-feature'
import { useAbTest } from '@marketplace-web/feature-flags/ab-tests-data'

import { clickEvent, favouriteItemEvent } from '@marketplace-web/item-page/item-page-data'
import { getHomepageSessionIdFromUrl } from '@marketplace-web/home/home-page-util'

import HeartIcon from '../HeartIcon'

type Props = {
  initialFavouriteCount: number
  isFavourited: boolean
  itemId: number
  itemUserId: number
}

const FavouriteIcon = ({ initialFavouriteCount, isFavourited, itemId, itemUserId }: Props) => {
  const { track } = useTracking()
  const translate = useTranslate('item')
  const { user } = useSession()
  const favouriteCountRemovalTest = useAbTest('favourite_count_removal')

  // TODO: Refactor after scaling userExternalId fetching
  const userExternalId = useGetUserExternalId()
  const shouldRenderFavouriteCount = favouriteCountRemovalTest?.variant !== 'on'

  const currentUserId = user?.id

  const { isFavourite, favouriteCount, toggleFavourite, hasFavouritedChanged } = useToggleFavourite(
    {
      entityId: itemId,
      isFavourite: isFavourited,
      count: initialFavouriteCount,
    },
  )

  const isViewingOwnItem = itemUserId === currentUserId

  const handleFavouriteToggleSuccess = (newIsFavourite: boolean) => {
    if (!newIsFavourite) return

    googleTagManagerTrack(GoogleTagManagerEvent.AddToFavourites, {
      item_id: itemId,
    })

    brazeLogCustomEvent({
      event: BrazeCustomEvent.AddedToFavourites,
      modelId: itemId,
      userExternalId,
    })
  }

  const handleClick = () => {
    const searchSession = getSearchSessionData()

    track(clickEvent({ target: 'favorite' }))

    track(
      favouriteItemEvent({
        itemId,
        isFollowEvent: !isFavourite,
        contentSource: 'user_items',
        globalSearchSessionId: searchSession.globalSearchSessionId,
        globalCatalogBrowseSessionId: searchSession.globalCatalogBrowseSessionId,
        homepageSessionId: getHomepageSessionIdFromUrl(),
      }),
    )

    toggleFavourite({
      onSuccess: handleFavouriteToggleSuccess,
    })
  }

  if (isViewingOwnItem) return null

  const getAriaLabel = () => {
    const textWithoutCount = translate(isFavourite ? 'a11y.favorited' : 'a11y.not_favorited')
    const textWithCount = translate(
      isFavourite ? 'a11y.unfavourite' : 'a11y.favourite',
      { count: favouriteCount },
      { count: favouriteCount },
    )

    if (!shouldRenderFavouriteCount) return textWithoutCount

    return favouriteCount > 0 ? textWithCount : textWithoutCount
  }

  const renderActionFeedback = () => (
    <span aria-live="polite" className="u-visually-hidden">
      {translate(isFavourite ? 'a11y.added_to_favorites' : 'a11y.removed_from_favorites')}
    </span>
  )

  return (
    <div className="u-position-absolute u-right u-bottom u-zindex-bump">
      <button
        aria-pressed={isFavourite}
        aria-label={getAriaLabel()}
        type="button"
        onClick={handleClick}
        data-testid="favourite-button"
        className="u-background-white u-flexbox u-align-items-center item-favourite-icon"
      >
        <HeartIcon isFavourite={isFavourite} isToggled={hasFavouritedChanged} isIconLarge />
        {favouriteCount > 0 && shouldRenderFavouriteCount && (
          <>
            <Spacer orientation={Spacer.Orientation.Vertical} size={Spacer.Size.Small} />
            <Text as="span" text={favouriteCount} type={Text.Type.Body} />
          </>
        )}
      </button>
      {renderActionFeedback()}
    </div>
  )
}

export default FavouriteIcon
